/**
 * "Active on" coverage check of the filter strip — kept as it was (UX
 * rethink § 3.1: `CoverageGapWarning` stays "tal cual"); only the link to
 * the matrices navigates in the SPA now (M8: zero `<a href>` in the tab).
 */
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Chip, Link, Typography } from '@mui/material'
import { AppNote } from '@components'
import { dsColorNew } from '@palette'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { displayDay } from '@utils'

// ---- Date-range coverage helpers ------------------------------------------
export function enumerateDays(fromISO, toISO) {
	if (!fromISO || !toISO) return []
	const from = new Date(fromISO + 'T00:00:00Z')
	const to   = new Date(toISO   + 'T00:00:00Z')
	if (isNaN(from) || isNaN(to) || from > to) return []
	const out = []
	for (let d = new Date(from); d <= to; d.setUTCDate(d.getUTCDate() + 1)) {
		out.push(d.toISOString().slice(0, 10))
	}
	return out
}
// A day is considered "covered" ONLY by a rule with an explicit date
// window. Wildcard rules (date_from = date_to = null) are baseline
// safety nets — they don't tell the operator anything about whether a
// specific day was thought about. This lets the gap warning surface
// "you forgot to set anything for the holidays".
export function ruleCoversDay(r, day) {
	const rFrom = r.date_from || null
	const rTo   = r.date_to   || null
	if (!rFrom && !rTo) return false           // wildcard is NOT specific coverage
	if (rFrom && day < rFrom) return false
	if (rTo   && day > rTo)   return false
	return true
}

// Human-readable compression of ISO days into contiguous runs.
// ["2026-09-08","2026-09-09","2026-09-11"] → ["Sep 8–9", "Sep 11"]
export function compressDayRanges(days) {
	if (!days.length) return []
	const sorted = [...days].sort()
	const runs = []
	let start = sorted[0], prev = sorted[0]
	for (let i = 1; i < sorted.length; i++) {
		const d = sorted[i]
		const prevDate = new Date(prev + 'T00:00:00Z')
		prevDate.setUTCDate(prevDate.getUTCDate() + 1)
		const expected = prevDate.toISOString().slice(0, 10)
		if (d !== expected) { runs.push([start, prev]); start = d }
		prev = d
	}
	runs.push([start, prev])
	return runs.map(([a, b]) => a === b ? fmtDay(a) : `${fmtDay(a)} – ${fmtDay(b)}`)
}
// The app's day, read from the ISO text so the UTC day stays the day
const fmtDay = (iso) => displayDay(iso)

export function CoverageGapWarning({ filters, uncovered }) {
	if (!filters.date_from || !filters.date_to) return null
	if (uncovered?.tooBig) {
		return (
			<AppNote severity="warning" sx={{ mb: 2 }}>
				<Box>
					<Typography sx={warningTitleSx}>
						Date range too wide to check coverage
					</Typography>
					<Typography sx={warningBodySx}>
						You selected {uncovered.count} days. Narrow the range to a year
						or less to see uncovered days here.
					</Typography>
				</Box>
			</AppNote>
		)
	}
	if (!Array.isArray(uncovered) || uncovered.length === 0) return null
	const runs = compressDayRanges(uncovered)
	return (
		<AppNote severity="warning" sx={{ mb: 2 }}>
			<Box>
				<Typography sx={warningTitleSx}>
					{uncovered.length === 1
					  ? '1 day in the selected range has no date-specific price limit'
					  : `${uncovered.length} days in the selected range have no date-specific price limit`}
				</Typography>
				<Typography sx={warningBodySx}>
					These days will only fall back to any wildcard rules you have
					(rules without dates). If you meant to set specific floors /
					ceilings for these dates — high-season, campaigns, etc. —
					you're missing them.
				</Typography>
				{/* This is the moment an operator discovers they need a second
				    season: the warning that says these days have no floor is
				    also where "duplicate the grid you already have" belongs
				    (UX, 2026-10-03). */}
				<Typography sx={{ ...warningBodySx, mt: 0.75 }}>
					A whole season's floors are usually a copy of another with a
					little on top:{' '}
					<Link component={RouterLink} to={`${DS_REPRICING_ROUTES.SETUP_PATH}?tab=price-matrix`}
					      sx={{ color: dsColorNew.noteAmber, fontWeight: 700, textDecorationColor: 'inherit' }}>
						duplicate a matrix with adjustments
					</Link>
					{' '}and attach the copy to these dates.
				</Typography>
				<Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
					{runs.slice(0, 20).map((run, i) => (
						<Chip key={i} size="small" label={run}
						      sx={{ height: 22, fontSize: 12, fontWeight: 500, borderRadius: '999px',
						            bgcolor: dsColorNew.white, color: dsColorNew.noteAmber,
						            border: `1px solid ${dsColorNew.noteAmberBorder}` }}/>
					))}
					{runs.length > 20 && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber,
						                  alignSelf: 'center' }}>
							…and {runs.length - 20} more ranges
						</Typography>
					)}
				</Box>
			</Box>
		</AppNote>
	)
}

const warningTitleSx = {
	fontWeight: 600, color: dsColorNew.noteAmber,
	fontSize: 13, mb: 0.5,
}
const warningBodySx = {
	color: dsColorNew.noteAmber, fontSize: 13, lineHeight: 1.45,
}
